/**
 * 17-field filter form for 患者情報検索 / Search Patient List.
 * Mirrors legacy RA2 /patients/search_patients.blade.php.
 */

import React, { useCallback, useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import {
  AppButton,
  AppCheckbox,
  AppFormLabel,
  AppInputNumber,
  AppMultiSelect,
  AppRadio,
  AppRadioGroup,
  AppSelect,
  AppTextField,
} from '@/components/atoms';
import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import { RA2NDatePicker } from '@/components/organisms';
import useAppToast from '@/hooks/useAppToast';
import {
  searchPatientListSchema,
  type SearchPatientListFormValues,
} from '@/schemas/searchPatientListSchema';
import { parseYearmonthParam } from '@/utils/dateConverter';
import { mbConvertKana, normalizeAddress } from '@/utils/japaneseText';
import type { MasterDataItem } from '../types';

const getCurrentYearmonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const DEFAULT_VALUES: SearchPatientListFormValues = {
  visit_from_yearmonth: getCurrentYearmonth(),
  visit_to_yearmonth: getCurrentYearmonth(),
  search_duration: 0,
  insurer_type_id: [],
  from_age: null,
  to_age: null,
  birth_month: 0,
  birth_duration: 0,
  sex_id: null,
  has_spouse: null,
  has_children: null,
  address: '',
  free_words: '',
  dm_reason_type_id: [],
  dm: false,
  include: false,
  sort_radio: 0,
};

interface Props {
  onSearch: (values: SearchPatientListFormValues) => void;
  insurerTypes: MasterDataItem[];
  sexes: MasterDataItem[];
  dmReasonTypes: MasterDataItem[];
  isLoading: boolean;
  totalCount?: number;
  initialValues?: SearchPatientListFormValues | null;
}

/** YYYYMM (6 digits) → YYYY-MM for RA2NDatePicker month mode value. */
const ymmToIso = (v: string): string =>
  v && v.length === 6 ? `${v.slice(0, 4)}-${v.slice(4)}` : '';

/**
 * RA2NDatePicker.onChange returns a wareki string ("令和08年04月") or empty.
 * Normalize via parseYearmonthParam → "YYYY-MM" → strip "-" for YYYYMM.
 */
const pickerValueToYmm = (v: string): string => {
  const iso = parseYearmonthParam(v || '');
  return iso ? iso.replace('-', '') : '';
};

const Label: React.FC<{ children: string }> = ({ children }) => (
  <AppFormLabel label={children} className="pt-1.5 !items-start !whitespace-normal" />
);

const normalizeAgeInput = (value: string | number | null): number | null => {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.min(999, Math.max(0, Math.trunc(n)));
};

const SearchPatientListForm: React.FC<Props> = ({
  onSearch,
  insurerTypes,
  sexes,
  dmReasonTypes,
  isLoading,
  totalCount = 0,
  initialValues,
}) => {
  const toast = useAppToast();
  const { control, handleSubmit, reset } = useTrimmedForm<SearchPatientListFormValues>({
    resolver: zodResolver(searchPatientListSchema),
    defaultValues: initialValues ?? DEFAULT_VALUES,
    mode: 'onSubmit',
  });

  const searchDuration = useWatch({ control, name: 'search_duration' });
  const birthDuration = useWatch({ control, name: 'birth_duration' });

  const insurerTypeOptions = useMemo(
    () => insurerTypes.map(t => ({ value: t.id, label: t.name })),
    [insurerTypes]
  );
  const dmReasonOptions = useMemo(
    () => dmReasonTypes.map(t => ({ value: t.id, label: t.name })),
    [dmReasonTypes]
  );
  const birthMonthOptions = useMemo(
    () => [
      { value: 0, label: 'なし' },
      ...Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: `${i + 1}月` })),
    ],
    []
  );

  const handleValidatedSearch = useCallback(
    (values: SearchPatientListFormValues) => {
      if (values.search_duration === 1 || values.search_duration === 2) {
        if (!values.visit_from_yearmonth) {
          toast.warning('【期間開始日】を入力してください');
          return;
        }
        if (!values.visit_to_yearmonth) {
          toast.warning('【期間終了日】を入力してください');
          return;
        }
      }
      onSearch(values);
    },
    [onSearch, toast]
  );

  const handleReset = useCallback(() => {
    reset(DEFAULT_VALUES);
    onSearch(DEFAULT_VALUES);
  }, [reset, onSearch]);

  return (
    <CollapsibleCard title="患者情報検索">
      <form onSubmit={handleSubmit(handleValidatedSearch)}>
        <div className="grid grid-cols-[minmax(5rem,1fr)_minmax(0,2fr)_minmax(5rem,1fr)_minmax(0,2fr)] items-start gap-x-3 gap-y-3">
          <Label>表示順</Label>
          <div className="min-w-0 col-span-3">
            <Controller
              name="sort_radio"
              control={control}
              render={({ field }) => (
                <AppRadioGroup
                  className="flex flex-wrap gap-x-2 gap-y-1"
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                >
                  <AppRadio value={0}>受診者番号順</AppRadio>
                  <AppRadio value={1}>カナ順</AppRadio>
                  <AppRadio value={2}>住所順</AppRadio>
                  <AppRadio value={3}>電話番号順</AppRadio>
                </AppRadioGroup>
              )}
            />
          </div>

          <Label>検索期間</Label>
          <div className="flex flex-wrap items-center min-w-0 col-span-3 gap-2">
            <Controller
              name="visit_from_yearmonth"
              control={control}
              render={({ field }) => (
                <RA2NDatePicker
                  picker="month"
                  className="w-36"
                  placeholder="年月指定"
                  disabled={searchDuration === 0}
                  value={ymmToIso(field.value || '')}
                  onChange={val => field.onChange(pickerValueToYmm(val))}
                />
              )}
            />
            <span className="text-gray-500">～</span>
            <Controller
              name="visit_to_yearmonth"
              control={control}
              render={({ field }) => (
                <RA2NDatePicker
                  picker="month"
                  className="w-36"
                  placeholder="年月指定"
                  disabled={searchDuration === 0}
                  value={ymmToIso(field.value || '')}
                  onChange={val => field.onChange(pickerValueToYmm(val))}
                />
              )}
            />
            <Controller
              name="search_duration"
              control={control}
              render={({ field }) => (
                <AppRadioGroup
                  className="flex flex-wrap gap-x-2 gap-y-1 xl:ml-2"
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                >
                  <AppRadio value={1}>施術期間</AppRadio>
                  <AppRadio value={2}>施術をしていない期間</AppRadio>
                  <AppRadio value={0}>指定無し</AppRadio>
                </AppRadioGroup>
              )}
            />
          </div>

          <Label>保険種別</Label>
          <div className="min-w-0 col-span-3">
            <Controller
              name="insurer_type_id"
              control={control}
              render={({ field }) => (
                <AppMultiSelect
                  className="w-full max-w-sm"
                  options={insurerTypeOptions}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="選択してください"
                  maxTagCount="responsive"
                />
              )}
            />
          </div>

          {/* 年齢 | 誕生日 */}
          <Label>年齢</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Controller
              name="from_age"
              control={control}
              render={({ field }) => (
                <AppInputNumber
                  className="w-24"
                  min={0}
                  max={999}
                  precision={0}
                  controls={false}
                  value={field.value ?? undefined}
                  onChange={v => field.onChange(normalizeAgeInput(v))}
                />
              )}
            />
            <span>歳</span>
            <span className="text-gray-500">～</span>
            <Controller
              name="to_age"
              control={control}
              render={({ field }) => (
                <AppInputNumber
                  className="w-24"
                  min={0}
                  max={999}
                  precision={0}
                  controls={false}
                  value={field.value ?? undefined}
                  onChange={v => field.onChange(normalizeAgeInput(v))}
                />
              )}
            />
            <span>歳</span>
          </div>
          <Label>誕生日</Label>
          <div className="flex flex-wrap items-center gap-2">
            <Controller
              name="birth_month"
              control={control}
              render={({ field }) => (
                <AppSelect
                  className="w-28"
                  options={birthMonthOptions}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={birthDuration === 0}
                />
              )}
            />
            <Controller
              name="birth_duration"
              control={control}
              render={({ field }) => (
                <AppRadioGroup
                  className="flex flex-wrap gap-x-2 gap-y-1"
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                >
                  <AppRadio value={1}>上旬</AppRadio>
                  <AppRadio value={2}>下旬</AppRadio>
                  <AppRadio value={0}>指定無し</AppRadio>
                </AppRadioGroup>
              )}
            />
          </div>

          {/* 性別 | 配偶者 */}
          <Label>性別</Label>
          <div>
            <Controller
              name="sex_id"
              control={control}
              render={({ field }) => (
                <div className="flex flex-wrap items-center gap-4 pt-1.5">
                  {sexes.map(s => (
                    <AppCheckbox
                      key={s.id}
                      checked={field.value === s.id}
                      onChange={e => field.onChange(e.target.checked ? s.id : null)}
                    >
                      {s.name}
                    </AppCheckbox>
                  ))}
                </div>
              )}
            />
          </div>
          <Label>配偶者</Label>
          <div>
            <Controller
              name="has_spouse"
              control={control}
              render={({ field }) => (
                <div className="flex flex-wrap items-center gap-4 pt-1.5">
                  <AppCheckbox
                    checked={field.value === 1}
                    onChange={e => field.onChange(e.target.checked ? 1 : null)}
                  >
                    有
                  </AppCheckbox>
                  <AppCheckbox
                    checked={field.value === 0}
                    onChange={e => field.onChange(e.target.checked ? 0 : null)}
                  >
                    無
                  </AppCheckbox>
                </div>
              )}
            />
          </div>

          <Label>子供</Label>
          <div className="col-span-3">
            <Controller
              name="has_children"
              control={control}
              render={({ field }) => (
                <div className="flex flex-wrap items-center gap-4">
                  <AppCheckbox
                    checked={field.value === 1}
                    onChange={e => field.onChange(e.target.checked ? 1 : null)}
                  >
                    有
                  </AppCheckbox>
                  <AppCheckbox
                    checked={field.value === 0}
                    onChange={e => field.onChange(e.target.checked ? 0 : null)}
                  >
                    無
                  </AppCheckbox>
                </div>
              )}
            />
          </div>

          {/* 住所 | フリーワード */}
          <Label>住所</Label>
          <div className="min-w-0">
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <AppTextField
                  {...field}
                  maxLength={255}
                  className="w-full min-w-0"
                  onBlur={e => {
                    field.onChange(normalizeAddress(e.target.value));
                    field.onBlur();
                  }}
                />
              )}
            />
          </div>
          <Label>フリーワード</Label>
          <div className="min-w-0">
            <Controller
              name="free_words"
              control={control}
              render={({ field }) => (
                <AppTextField
                  {...field}
                  maxLength={255}
                  className="w-full min-w-0"
                  onBlur={e => {
                    field.onChange(mbConvertKana(e.target.value, 'SANK'));
                    field.onBlur();
                  }}
                />
              )}
            />
          </div>

          {/* 当院を知った理由 | DM許可 */}
          <Label>当院を知った理由</Label>
          <div className="min-w-0">
            <Controller
              name="dm_reason_type_id"
              control={control}
              render={({ field }) => (
                <AppMultiSelect
                  className="w-full max-w-sm"
                  options={dmReasonOptions}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="選択してください"
                  maxTagCount="responsive"
                />
              )}
            />
          </div>
          <Label>DM許可</Label>
          <div className="pt-1.5">
            <Controller
              name="dm"
              control={control}
              render={({ field }) => (
                <AppCheckbox
                  checked={!!field.value}
                  onChange={e => field.onChange(e.target.checked)}
                >
                  DM不可の患者を除く
                </AppCheckbox>
              )}
            />
          </div>

          <Label>除外</Label>
          <div className="col-span-3 pt-1.5">
            <Controller
              name="include"
              control={control}
              render={({ field }) => (
                <AppCheckbox
                  checked={!!field.value}
                  onChange={e => field.onChange(e.target.checked)}
                >
                  含める
                </AppCheckbox>
              )}
            />
          </div>
        </div>

        {/* 3-col action row: spacer | centered buttons | right-aligned count badge */}
        <div className="grid grid-cols-1 items-center gap-3 pt-4 mt-5 border-t border-gray-100 sm:grid-cols-[1fr_auto_1fr]">
          <div className="hidden sm:block" />
          <div className="flex flex-wrap items-center justify-center gap-3">
            <AppButton type="primary" htmlType="submit" loading={isLoading}>
              <i className="mr-1 fa fa-search" aria-hidden /> 検索
            </AppButton>
            <AppButton type="default" htmlType="button" onClick={handleReset} disabled={isLoading}>
              リセット
            </AppButton>
          </div>
          <div className="flex justify-center sm:justify-end">
            <span className="px-3 py-1 text-sm font-medium leading-tight text-center text-black rounded-md bg-amber-400">
              該当件数 {totalCount}
            </span>
          </div>
        </div>
      </form>
    </CollapsibleCard>
  );
};

export default SearchPatientListForm;
